import { useState, useEffect, useCallback } from 'react'
import { SchoolLookUpSiteData, NetSuiteCustomer } from 'types'
import ssapi from 'lib/api/secondstepApi'

interface ShowRequiredState {
  city: boolean
  country: boolean
  netsuiteCustomerId: boolean
  nsCustomerId: boolean
  organizationName: boolean
  region: boolean
}

export const useSchoolLookUp = (
  initialSiteData: SchoolLookUpSiteData,
  onSelection: (data: SchoolLookUpSiteData) => void,
  fireOnLoad: boolean = true,
) => {
  const [selectedSiteData, setSelectedSiteData] =
    useState<SchoolLookUpSiteData>(() => {
      if (initialSiteData) {
        // If we have an organizationName but no netsuiteCustomerId,
        // set netsuiteCustomerId to 0 (not listed)
        // Note: We need to check for null/undefined explicitly since 0 is falsy
        const netsuiteCustomerId =
          initialSiteData.netsuiteCustomerId !== null &&
          initialSiteData.netsuiteCustomerId !== undefined
            ? initialSiteData.netsuiteCustomerId
            : initialSiteData.organizationName
            ? 0
            : null

        return {
          city: initialSiteData.city || '',
          companyName: initialSiteData.companyName || '',
          country: initialSiteData.country || '',
          netsuiteCustomerId,
          nsCustomerId: initialSiteData.nsCustomerId || '',
          organizationName: initialSiteData.organizationName || '',
          region: initialSiteData.region || '',
        }
      }
      return {
        city: '',
        companyName: '',
        country: '',
        netsuiteCustomerId: null,
        nsCustomerId: '',
        organizationName: '',
        region: '',
      }
    })

  const [hasRegionData, setHasRegionData] = useState<boolean>(true)
  const [isListed, setIsListed] = useState<boolean>(false)
  const [showRequired, setShowRequired] = useState<ShowRequiredState>({
    country: false,
    region: false,
    city: false,
    nsCustomerId: false,
    netsuiteCustomerId: false,
    organizationName: false,
  })
  const [netSuiteCustomersData, setNetSuiteCustomersData] = useState<
    NetSuiteCustomer[] | null
  >(null)

  // Call onSelection on mount if fireOnLoad is true and we have initial data
  // But only if we have a netsuiteCustomerId (meaning data is already loaded)
  // or if we don't have an nsCustomerId (meaning no lookup is needed)
  useEffect(() => {
    if (initialSiteData && fireOnLoad) {
      // Only fire onLoad if we have complete data or no lookup is needed
      const hasCompleteData = initialSiteData.netsuiteCustomerId !== null
      const noLookupNeeded = !initialSiteData.nsCustomerId

      if (hasCompleteData || noLookupNeeded) {
        onSelection(selectedSiteData)
      }
    }
  }, []) // Only run on mount

  // Update site data when initialSiteData changes
  useEffect(() => {
    if (initialSiteData) {
      // If we have an organizationName but no netsuiteCustomerId,
      // set netsuiteCustomerId to 0 (not listed)
      // Note: We need to check for null/undefined explicitly since 0 is falsy
      const netsuiteCustomerId =
        initialSiteData.netsuiteCustomerId !== null &&
        initialSiteData.netsuiteCustomerId !== undefined
          ? initialSiteData.netsuiteCustomerId
          : initialSiteData.organizationName
          ? 0
          : null

      const newData = {
        city: initialSiteData.city || '',
        companyName: initialSiteData.companyName || '',
        country: initialSiteData.country || '',
        netsuiteCustomerId,
        nsCustomerId: initialSiteData.nsCustomerId || '',
        organizationName: initialSiteData.organizationName || '',
        region: initialSiteData.region || '',
      }
      setSelectedSiteData(newData)
    }
  }, [initialSiteData])

  // Check if school is listed in NetSuite customers - simplified
  useEffect(() => {
    if (!netSuiteCustomersData) {
      setIsListed(false)
      return
    }

    const hasValidNetsuiteId =
      selectedSiteData.netsuiteCustomerId &&
      selectedSiteData.netsuiteCustomerId !== 0
    const hasValidNsCustomerId =
      selectedSiteData.nsCustomerId && selectedSiteData.nsCustomerId !== ''

    if (hasValidNetsuiteId) {
      const isFound = netSuiteCustomersData.some(
        customer =>
          customer.id === selectedSiteData.netsuiteCustomerId &&
          customer.id !== 0,
      )
      setIsListed(isFound)
    } else if (hasValidNsCustomerId) {
      const isFound = netSuiteCustomersData.some(
        customer =>
          customer.nsCustomerId === selectedSiteData.nsCustomerId &&
          customer.id !== 0,
      )
      setIsListed(isFound)
    } else {
      setIsListed(false)
    }
  }, [
    selectedSiteData.netsuiteCustomerId,
    selectedSiteData.nsCustomerId,
    netSuiteCustomersData,
  ])

  const handleFieldChange = useCallback(
    (field: string, value: string) => {
      const newState: Partial<SchoolLookUpSiteData> = { [field]: value }

      if (field === 'city') {
        newState.country = selectedSiteData.country
        newState.nsCustomerId = ''
        newState.organizationName = ''
        newState.region = selectedSiteData.region
      }
      if (field === 'region') {
        newState.city = ''
        newState.country = selectedSiteData.country
        newState.nsCustomerId = ''
        newState.organizationName = ''
      }
      if (field === 'country') {
        newState.city = ''
        newState.nsCustomerId = ''
        newState.organizationName = ''
        newState.region = ''
      }

      const updatedData = { ...selectedSiteData, ...newState }
      setSelectedSiteData(updatedData)
      onSelection(updatedData)
    },
    [selectedSiteData, onSelection],
  )

  const handleLoadStateData = useCallback((data: any[]) => {
    setHasRegionData(data.length > 1)
    return data
  }, [])

  const handleLoadSchoolsData = useCallback((data: any[]) => {
    setNetSuiteCustomersData(data)
    return data
  }, [])

  const handleSchoolSelect = useCallback(
    (netSuiteCustomer: NetSuiteCustomer) => {
      const {
        id: netsuiteCustomerId,
        nsCustomerId,
        companyName,
      } = netSuiteCustomer
      const isListed = netsuiteCustomerId !== 0

      const newSelection = {
        ...selectedSiteData,
        companyName: isListed ? companyName : '',
        netsuiteCustomerId,
        nsCustomerId,
        // When "My school is not listed" is selected (netsuiteCustomerId === 0),
        // preserve the organizationName if it exists, otherwise keep it as is
        organizationName: isListed
          ? ''
          : selectedSiteData.organizationName || '',
      }

      setSelectedSiteData(newSelection)
      onSelection(newSelection)
    },
    [selectedSiteData, onSelection],
  )

  const handleSchoolNameChange = useCallback(
    (organizationName: string) => {
      const nsCustomerId = selectedSiteData.nsCustomerId || ''
      const netsuiteCustomerId = selectedSiteData.netsuiteCustomerId || 0

      const newNameChange = {
        ...selectedSiteData,
        organizationName,
        netsuiteCustomerId,
        nsCustomerId,
      }
      setSelectedSiteData(newNameChange)
      onSelection(newNameChange)
    },
    [selectedSiteData, onSelection],
  )

  const selectCitiesEndpoint = useCallback(() => {
    if (!selectedSiteData.country) {
      return Promise.resolve({ status: 200, data: [] })
    }

    return ssapi.getCities(selectedSiteData.country, selectedSiteData.region)
  }, [selectedSiteData.region, selectedSiteData.country])

  const selectSchoolsEndpoint = useCallback(() => {
    if (!selectedSiteData.city) {
      return Promise.resolve({ status: 200, data: [] })
    }

    const params: any = {
      country: selectedSiteData.country,
      city: selectedSiteData.city,
    }

    if (hasRegionData && selectedSiteData.region) {
      params.region = selectedSiteData.region
    }

    return ssapi.getNetSuiteCustomers(params)
  }, [
    selectedSiteData.city,
    selectedSiteData.country,
    selectedSiteData.region,
    hasRegionData,
  ])

  const checkRequired = useCallback(
    (field: string, value: string) => {
      if (field === 'region') {
        setShowRequired(prev => ({
          ...prev,
          region: hasRegionData && value === '',
        }))
        return
      }
      setShowRequired(prev => ({ ...prev, [field]: value === '' }))
    },
    [hasRegionData],
  )

  const isInNetSuiteCustomersData = useCallback(() => {
    if (!netSuiteCustomersData) return false

    if (selectedSiteData.netsuiteCustomerId) {
      return netSuiteCustomersData.some(
        nsc => nsc.id === selectedSiteData.netsuiteCustomerId,
      )
    }

    if (selectedSiteData.nsCustomerId) {
      return netSuiteCustomersData.some(
        nsc => nsc.nsCustomerId === selectedSiteData.nsCustomerId,
      )
    }

    return false
  }, [
    selectedSiteData.netsuiteCustomerId,
    selectedSiteData.nsCustomerId,
    netSuiteCustomersData,
  ])

  const getNetsuiteCustomerId = useCallback(() => {
    if (selectedSiteData.netsuiteCustomerId === null) return ''

    // If netsuiteCustomerId is 0 (My school is not listed), return '0' as string
    // HTML select elements use string values, so we need to convert to string
    if (selectedSiteData.netsuiteCustomerId === 0) return '0'

    // For other values, check if it's in the data
    const value = !isInNetSuiteCustomersData()
      ? 0
      : selectedSiteData.netsuiteCustomerId
    // Convert to string for HTML select element
    return String(value)
  }, [isInNetSuiteCustomersData, selectedSiteData.netsuiteCustomerId])

  const getNetSuiteCustomerObject = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedValue = event.target.value

      // Handle empty selection case
      if (selectedValue === '' || selectedValue === 'blank') {
        return {
          id: null,
          nsCustomerId: '',
          companyName: '',
        }
      }

      const selectedId = parseInt(selectedValue, 10)

      // Handle "My School is Not Listed" case (value is '0' as string)
      if (selectedId === 0 || selectedValue === '0') {
        return {
          id: 0,
          nsCustomerId: '',
          companyName: '',
        }
      }

      const netSuiteCustomer = netSuiteCustomersData?.find(
        netSuiteCustomer => netSuiteCustomer.id === selectedId,
      )

      return {
        id: (netSuiteCustomer && netSuiteCustomer.id) || 0,
        nsCustomerId: (netSuiteCustomer && netSuiteCustomer.nsCustomerId) || '',
        companyName: (netSuiteCustomer && netSuiteCustomer.companyName) || '',
      }
    },
    [netSuiteCustomersData],
  )

  return {
    checkRequired,
    getNetSuiteCustomerObject,
    getNetsuiteCustomerId,
    handleFieldChange,
    handleLoadStateData,
    handleLoadSchoolsData,
    handleSchoolNameChange,
    handleSchoolSelect,
    hasRegionData,
    isListed,
    netSuiteCustomersData,
    selectCitiesEndpoint,
    selectSchoolsEndpoint,
    selectedSiteData,
    setNetSuiteCustomersData,
    showRequired,
  }
}
